import React from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import QuestionTitleImage from '../QuestionTitleImage'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'
import DOMPurify from 'dompurify'

class NumericRatingScale extends React.Component {
  constructor(props) {
    super(props)
    const { question } = this.props
    const { choices_order } = question
    this.state = {
      selected: null,
      selectingChoice: null,
      deselectingChoice: null,
      completed: false,
      style: { width: `${65 / choices_order.length}vw` },
      tallestLabelHeight: 0,
      isOptOutSelected: false,
    }
    this.labelRefs = {}
  }

  componentDidMount() {
    const { storedAnswer, question, suppressAutoAdvance } = this.props
    if (storedAnswer?.[0]?.choice_id) {
      this.onSelectChoice(storedAnswer?.[0]?.choice_id)
      if (!suppressAutoAdvance) {
        this.onCompleteNumericRatingScaleQuestion()
      }
    } else if (storedAnswer?.[0]?.label === 'opt-out') {
      this.setState({ isOptOutSelected: true }, () => {
        this.onSetCompletion()
        if (!suppressAutoAdvance) {
          this.onCompleteNumericRatingScaleQuestion()
        }
      })
    }

    this.calculateAndSetLabelWidth()
    window.addEventListener('resize', this.onResize)
  }

  componentDidUpdate(prevProps, prevState) {
    const { selected } = this.state

    if (selected !== prevState.selected) {
      this.setState({ deselectingChoice: prevState.selected })
      // Animation cleanup handled by onAnimationEnd event
    }
    this.onResize()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize)
  }

  persistNumericRatingScaleSubmission = () => {
    const { question, id, webSurvey, completeQuestion } = this.props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const answer = this.createAnswerArr()
    const hasAnswer = answer.length > 0
    const { label, type, attributes } = question
    const { required } = attributes
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: hasAnswer ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    try {
      completeQuestion(submission, webSurvey.order?.[0])
    } catch (e) {
      console.warn('NumericRatingScale autosave failed', { error: e })
    }
  }

  createAnswerArr = () => {
    const { question, id } = this.props
    const { choices, attributes } = question
    const { opt_out } = attributes
    const { selected, isOptOutSelected } = this.state
    if (selected) {
      const { value } = choices[selected]
      return [
        {
          choice_id: selected,
          label: choices[selected].label,
          question: id,
          value,
        },
      ]
    }
    if (isOptOutSelected) {
      return [
        {
          label: 'opt-out',
          question: id,
          value: opt_out,
        },
      ]
    }
    return []
  }

  onCompleteNumericRatingScaleQuestion = () => {
    const {
      question,
      id,
      completeQuestion,
      nextQ,
      webSurvey,
      submissionSurvey,
      nextQId,
      reviewFunctionalitySupported,
      isConsent = false,
      isLastQuestion = false,
      isSibling = false,
    } = this.props
    const { selected, isOptOutSelected } = this.state
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = this.createAnswerArr()
    const hasAnswer = !!selected || isOptOutSelected
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: hasAnswer ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    const currentAnswer = { [id]: answer }

    const shouldRenderNextQ = getShouldRenderNextQuestion({
      isConsent,
      isLastQuestion,
      isSibling,
    })

    checkLogicAndComplete(
      webSurvey,
      id,
      submissionSurvey,
      currentAnswer,
      nextQId,
      nextQ,
      completeQuestion,
      submission,
      reviewFunctionalitySupported,
      shouldRenderNextQ,
    )
  }

  onSetCompletion = () => {
    const { selected, isOptOutSelected } = this.state
    this.setState({ completed: !!selected || isOptOutSelected })
  }

  onSelectChoice = (choice) => {
    const { setAllRequiredAnswered, id } = this.props
    this.setState({ selected: choice, selectingChoice: choice }, () => {
      setAllRequiredAnswered(false, id)
      this.onSetCompletion()
      this.persistNumericRatingScaleSubmission()
    })
    // Animation cleanup handled by onAnimationEnd event
  }

  handleAnimationEnd = (e) => {
    // Only handle our specific animation to avoid interfering with child animations
    if (e.animationName === 'selecting') {
      const { selectingChoice, deselectingChoice } = this.state
      if (selectingChoice) {
        this.setState({ selectingChoice: null })
      }
      if (deselectingChoice) {
        this.setState({ deselectingChoice: null })
      }
    }
  }

  onOptOutToggle = () => {
    const { setAllRequiredAnswered, id } = this.props
    const { isOptOutSelected } = this.state

    this.setState(
      {
        isOptOutSelected: !isOptOutSelected,
        selected: null,
      },
      () => {
        setAllRequiredAnswered(false, id)
        this.onSetCompletion()
        this.persistNumericRatingScaleSubmission()
      },
    )
  }

  calculateMinAdjacentLabelDistance = () => {
    const { question } = this.props
    const { choices_order, choices } = question
    let min = 10
    let lastIndexWithLabel = null
    choices_order.forEach((choiceId, index) => {
      if (choices[choiceId].label !== '') {
        if (lastIndexWithLabel || lastIndexWithLabel === 0) {
          min = Math.min(index - lastIndexWithLabel, min)
        }
        lastIndexWithLabel = index
      }
    })
    return min
  }

  calculateAndSetLabelWidth = () => {
    const { question } = this.props
    const { style } = this.state
    const numChoices = question.choices_order.length
    const minAdjacentLabelDistance = this.calculateMinAdjacentLabelDistance()
    const questionWidth = 65
    // .8 multiplier acts as a buffer for labels not to overlap
    const labelWidth =
      (questionWidth / Math.ceil(numChoices / minAdjacentLabelDistance)) * 0.8
    this.setState({ style: { ...style, width: `${labelWidth}vw` } })
  }

  setLabelRef = (id, ref) => {
    this.labelRefs[id] = ref
  }

  getTallestLabel = () => {
    let tallestLabelHeight = 0
    for (const ref of Object.values(this.labelRefs)) {
      if (ref?.clientHeight > tallestLabelHeight) {
        tallestLabelHeight = ref.clientHeight
      }
    }
    return tallestLabelHeight
  }

  onResize = () => {
    const { tallestLabelHeight } = this.state
    const newHeight = this.getTallestLabel()
    if (tallestLabelHeight !== newHeight) {
      this.setState({ tallestLabelHeight: newHeight })
    }
  }

  renderChoices = () => {
    const { question } = this.props
    const { choices, choices_order } = question
    const {
      selected,
      selectingChoice,
      deselectingChoice,
      style,
      tallestLabelHeight,
      isOptOutSelected,
    } = this.state
    const hasChoiceImages = choices_order.some(
      (choiceId) => !!choices[choiceId]?.imageSrc,
    )
    const choicesStyle = {
      marginBottom: `${tallestLabelHeight + (hasChoiceImages ? 140 : 50)}px`,
    }

    return (
      <div
        className={`numeric-rating-scale-choices ${
          isOptOutSelected ? 'disabled' : ''
        }`}
        style={choicesStyle}>
        {choices_order.map((choice) => {
          const { label, imageSrc } = choices[choice]
          const checked = choice === selected
          const hasLabelContent = !!label || !!imageSrc
          return (
            <div className='label-container' key={`${choice}`}>
              <Button
                onClick={() => this.onSelectChoice(choice)}
                onAnimationEnd={this.handleAnimationEnd}
                className={`choice${selectingChoice === choice ? ' selecting' : ''}${
                  selected === choice ? ' selected' : ''
                }${deselectingChoice === choice ? ' deselecting' : ''}`}
                disabled={isOptOutSelected}>
                <Checkbox
                  checked={checked}
                  transition={
                    choice === selectingChoice || choice === deselectingChoice
                  }
                />
                <span className={checked ? 'selected' : ''}>
                  {choices[choice].value}
                </span>
              </Button>
              {hasLabelContent && <div className='arrow-up' />}
              <div
                className='label-cell'
                key={choice}
                ref={(ref) => this.setLabelRef(choice, ref)}>
                <div className='label-content' style={style}>
                  {label && (
                    <p
                      dangerouslySetInnerHTML={{
                        __html: `<strong>${formatAndSanitizeHTML(label)}</strong>`,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  renderImages = () => {
    const { question } = this.props
    const { choices, choices_order } = question
    const hasImages = choices_order.some(
      (choiceId) => !!choices[choiceId]?.imageSrc,
    )

    if (!hasImages) return null

    return (
      <div className='nrs-images-row'>
        {choices_order.map((choice) => {
          const { imageSrc } = choices[choice]
          return (
            <div className='image-cell' key={choice}>
              {imageSrc && (
                <img
                  className='choice-image'
                  src={imageSrc}
                  alt={`choice-${choice}`}
                />
              )}
            </div>
          )
        })}
      </div>
    )
  }

  renderLabels = () => {
    const { question } = this.props
    const { choices, choices_order } = question
    return (
      <div className='label-row'>
        {choices_order.map((choice) => {
          const { label } = choices[choice]
          return (
            <div className='label-cell' key={choice}>
              <p>
                <b>{label}</b>
              </p>
            </div>
          )
        })}
      </div>
    )
  }

  checkIsBtnDisabled = () => {
    const { question } = this.props
    const { completed, isOptOutSelected } = this.state
    const { attributes } = question
    const { required } = attributes

    if (isOptOutSelected) {
      return false
    }
    return required && !completed
  }

  render() {
    const {
      className,
      question,
      id,
      renderBackButton,
      strings,
      forwardedRef,
      isConsent,
      isLastQuestion,
      submissionLoading,
      submissionSurvey,
      webSurvey,
      isSibling = false,
    } = this.props
    const { completed, isOptOutSelected } = this.state
    const { label, attributes, choices, choices_order, imageSrc } = question
    const { required, opt_out } = attributes
    const _className = className ? ` ${className}` : ''
    const hasChoiceImages = choices_order.some(
      (choiceId) => !!choices[choiceId]?.imageSrc,
    )

    return (
      <SurveyQuestion id={id}>
        <MakeScrollable>
          <div
            ref={forwardedRef}
            className={`survey-question numeric-rating-scale${_className}`}>
            <div
              className={`nrs-grid-container${hasChoiceImages ? ' has-choice-images' : ''
                }`}>
              <div className='nrs-question-row'>
                <p className='question-label'>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: formatAndSanitizeHTML(label),
                    }}
                  />
                  <QuestionTitleImage imageSrc={imageSrc} questionId={id} />
                </p>
              </div>
              <div className='nrs-choices-row'>{this.renderChoices()}</div>
              {this.renderImages()}
              {!!opt_out && (
                <div className='nrs-opt-out-row'>
                  <div className='opt-out-checkbox-wrapper'>
                    <Checkbox
                      checked={isOptOutSelected}
                      onClick={this.onOptOutToggle}
                      label={opt_out}
                      check
                    />
                  </div>
                </div>
              )}
              {!isSibling && (
                <div className='nrs-buttons-row'>
                  <QuestionFooter
                    completed={completed}
                    id={id}
                    isConsent={isConsent}
                    isLastQuestion={isLastQuestion}
                    loading={submissionLoading}
                    onComplete={() =>
                      this.onCompleteNumericRatingScaleQuestion()
                    }
                    question={question}
                    renderBackButton={renderBackButton}
                    strings={strings}
                    submissionSurvey={submissionSurvey}
                    webSurvey={webSurvey}
                  />
                </div>
              )}
            </div>
          </div>
        </MakeScrollable>
      </SurveyQuestion>
    )
  }
}

const NumericRatingScaleWithRef = React.forwardRef((props, ref) => (
  <NumericRatingScale {...props} forwardedRef={ref} />
))

export default NumericRatingScaleWithRef

NumericRatingScale.propTypes = {
  className: PropTypes.string,
  completeQuestion: PropTypes.func,
  id: PropTypes.string,
  nextQ: PropTypes.func,
  nextQId: PropTypes.func,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  setAllRequiredAnswered: PropTypes.func,
  submissionSurvey: PropTypes.object,
  webSurvey: PropTypes.object,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}
